/**
 * F4 gate phone strings (English), spread into en.ts. Kept in their own module so the F5 store
 * strings can grow in parallel. The original tile keys (gate.openDay … gate.closeDayHint) stay in
 * en.ts. Hindi: gate.hi.ts (typed to have every key here).
 */
export const gateEn = {
  'gate.change': 'Change',
  'gate.saveFailed': 'Could not save on the phone',

  // fields
  'gate.field.village': 'Village',
  'gate.field.date': 'Date',
  'gate.field.scale': 'Scale',
  'gate.field.testWeight': 'Test weight',
  'gate.field.scaleShows': 'Scale shows',
  'gate.field.rateCard': 'Rate card',
  'gate.field.crop': 'Crop',
  'gate.field.field': 'Field',
  'gate.field.broughtBy': 'Brought by',

  // roster
  'gate.roster.update': 'Update farmer list',
  'gate.roster.updated': 'Farmer list updated',
  'gate.roster.needsNetwork': 'Needs network. Update the day before, where there is signal.',
  'gate.roster.stale':
    'Farmer list is more than 24 hours old. Farmers enrolled since then are not on it.',

  // day status
  'gate.day.title': 'Collection day',
  'gate.day.hint': 'Day status, tags in hand, spare roll',
  'gate.day.noneOpen': 'No collection day is open on this phone.',
  'gate.day.lastClosed': 'Last day {code} closed {when}',
  'gate.day.open': 'Day open',
  'gate.day.statusOpen': 'Open',
  'gate.day.operator': 'Operator',
  'gate.day.opened': 'Opened at',
  'gate.day.stampRed': 'Stamp',
  'gate.day.tagsLeft': 'Tags left',
  'gate.day.sealsLeft': 'Seals left',
  'gate.day.leftOf': '{left} of {total}',
  'gate.day.lots': 'Lots (good + red-tie)',
  'gate.day.lotsHint': 'No money on this phone',
  'gate.day.produce': 'Produce today',
  'gate.day.draftOpen': 'Delivery in progress: {name}',
  'gate.day.resume': 'Resume',
  'gate.day.deliveries': 'Deliveries ({count})',
  'gate.day.spare': 'Open spare roll',
  'gate.day.voidTag': 'Void a tag',

  // open day (F4.1)
  'gate.open.openDay': 'Open day',
  'gate.open.opened': 'Day opened',
  'gate.open.blockedTitle': 'Last day did not add up',
  'gate.open.blocked':
    'The phone will not open a new day until the supervisor clears it in the console.',
  'gate.open.noCard': 'Update farmer list and rate cards: no usable price on the phone.',
  'gate.open.pickScale': 'Pick the scale.',
  'gate.open.typeTest': 'Type what the scale shows with the test weight.',
  'gate.open.testOff': 'Scale is {diff} off. Level it and try again, or use the spare scale.',
  'gate.open.testOffShort': 'More than 0.100 kg off — the day will not open.',
  'gate.open.needRoll': 'Scan the first and last tag of your roll.',
  'gate.open.needSeals': 'Scan the first and last seal of your strip.',
  'gate.open.stampDue': 'Stamp due {date}',
  'gate.open.noStamp': 'No stamp date',
  'gate.open.stampPassed': 'Stamp date has passed: the day opens, the supervisor is told.',
  'gate.open.notRegistered': 'Not registered',
  'gate.open.unregisteredWarn': 'An unregistered scale is allowed in October and reported.',

  // rolls
  'gate.roll.tagRoll': 'Tag roll',
  'gate.roll.sealStrip': 'Seal strip',
  'gate.roll.tag': 'Tag',
  'gate.roll.seal': 'Seal',
  'gate.roll.firstTag': 'First tag',
  'gate.roll.lastTag': 'Last tag',
  'gate.roll.firstSeal': 'First seal',
  'gate.roll.lastSeal': 'Last seal',
  'gate.roll.confirmRoll': 'Confirm roll',
  'gate.roll.confirmSeals': 'Confirm seals',
  'gate.roll.tagsInHand': '{count} {count:tag|tags} in hand',
  'gate.roll.sealsInHand': '{count} {count:seal|seals} in hand',
  'gate.roll.unknown': 'Not a roll of this village.',
  'gate.roll.wrongKind': 'Wrong kind: a seal scanned as a tag, or a tag as a seal.',
  'gate.roll.different': 'First and last are from different rolls.',
  'gate.roll.order': 'The last number is before the first.',
  'gate.roll.notAssigned':
    'This roll is not assigned to you. The office assigns it, or the supervisor can with his PIN.',

  // supervisor PIN
  'gate.sup.title': 'Supervisor PIN',
  'gate.sup.who': 'Which supervisor?',
  'gate.sup.none': 'No supervisor with a PIN on this phone. Update the farmer list.',
  'gate.sup.assignRoll': 'Assign this roll to the operator on the spot.',
  'gate.sup.assignButton': 'Supervisor assigns',

  // refusals
  'gate.refuse.unknown':
    'Not from your roll. Put it aside for the supervisor and use the next one.',
  'gate.refuse.wrongKind': 'Wrong kind: a seal is not a bag tag (and the reverse).',
  'gate.refuse.notConfirmed': 'This roll is not confirmed today. Open it as a spare roll first.',
  'gate.refuse.void': 'This code was voided. It can never be used.',
  'gate.refuse.used': 'This code is already on another bag or sample.',
  'gate.refuse.duplicate': 'Already scanned on a bag of this delivery.',

  // delivery
  'gate.delivery.noDay': 'Open the collection day first.',
  'gate.delivery.staleDraft':
    'A delivery {code} from a closed day is still on this phone. Call the supervisor.',
  'gate.step.details': 'Delivery details',
  'gate.step.check': 'Check bags',
  'gate.step.moisture': 'Moisture check',
  'gate.step.price': 'Price',
  'gate.step.tag': 'Tag and weigh',
  'gate.step.sample': 'Seal the sample',
  'gate.step.slip': 'Your account and slip',
  'gate.step.close': 'Close delivery',

  // find farmer (F4.2)
  'gate.farmer.title': 'Find farmer',
  'gate.farmer.name': 'Name',
  'gate.farmer.nameHint': 'First letters, Hindi or English',
  'gate.farmer.rosterNo': 'Roster no.',
  'gate.farmer.last4': 'Phone last 4 digits',
  'gate.farmer.last4Hint': 'Ask him the last 4 digits of his phone.',
  'gate.farmer.careOf': 'S/o {name}',
  'gate.farmer.noPhone': 'No phone on file: check the photo only.',
  'gate.farmer.noPhoneShort': 'no phone',
  'gate.farmer.phoneMismatch':
    'Digits do not match. Carry on only if the photo is him; it is counted.',
  'gate.farmer.notOnList': 'Not on the list. Do not start a delivery; call the supervisor.',
  'gate.farmer.isThisHim': 'Is this him?',
  'gate.farmer.yes': 'Yes, this is him',
  'gate.farmer.no': 'No, search again',

  // details
  'gate.details.noCrops': 'No crop registered',
  'gate.details.area': 'Area {area}',
  'gate.details.anotherField': 'Another field (not registered)',
  'gate.details.anotherFieldWarn':
    'The whole delivery becomes ordinary: never sold under the brand.',
  'gate.details.himself': 'Himself',
  'gate.details.someoneElse': 'Someone else',
  'gate.details.delivererName': "Deliverer's name",
  'gate.details.delivererPhone': "Deliverer's phone",
  'gate.details.delivererPhoto': "Deliverer's photo",
  'gate.details.tenDigits': '10 digits',
  'gate.storage.q1': 'Stored at home over 7 days?',
  'gate.storage.q2': 'Any tablet or powder used?',
  'gate.storage.riskNote':
    'Carry on. His good-bag lot will be held for a check; his price does not change.',
  'gate.answer.YES': 'Yes',
  'gate.answer.NO': 'No',
  'gate.answer.DONT_KNOW': "Don't know",
  'gate.season.label': 'Season light',
  'gate.season.green': 'Green',
  'gate.season.amber': 'Amber: his payment will wait for the office',

  // check bags (F4.3)
  'gate.check.brought': 'Bags brought',
  'gate.check.broughtHint': 'Count all bags before opening any.',
  'gate.check.belowTagged': '{count} {count:bag is|bags are} already tagged.',
  'gate.check.confirmMany': 'Yes, {count} {count:bag|bags}',
  'gate.check.failedBags': 'Failed bags (red tie)',
  'gate.check.failedN': 'Failed bag {n}',
  'gate.check.reason': 'Red-line reason',
  'gate.check.photo': 'Photo of the inside',
  'gate.check.photoHint': 'Required for each failed bag.',
  'gate.check.addFailed': '+ Add failed bag',
  'gate.check.removeFailed': 'Remove failed bag',
  'gate.check.redTie': 'Tie a red tie on each failed bag. They go into a second, ordinary lot.',
  'gate.check.goodBags': 'Good bags',
  'gate.check.failedCount': 'Red-tie bags',
  'gate.check.allFailed': 'All bags failed: one ordinary lot, no sample.',
  'gate.check.moistureBags': 'Bags for moisture draws',
  'gate.check.bagPair': 'Bag {a} and bag {b}',
  'gate.check.bagOne': 'Bag {a} (two draws)',
  'gate.check.probe': 'Probe draws',
  'gate.check.probeDone': 'A probe draw from the middle of every good bag is in the sample bag.',
  'gate.check.probeUndo': 'Undo',
  // close checks (F4.10)
  'gate.check.storage': 'Storage answers',
  'gate.check.bagsCounted': 'Bags counted',
  'gate.check.failedBagsOk': 'Each failed bag has a reason and a photo',
  'gate.check.probeOk': 'Probe draws in the sample bag',
  'gate.check.accepted': 'Price accepted',
  'gate.check.tags': 'Tags = bags for {lot}: {tagged} of {bags}',
  'gate.check.weights': 'Sack type and weight on every bag',
  'gate.check.moisture': 'Moisture read',
  'gate.check.sample': 'Sample sealed, weighed (min {min} g) and photographed',
  'gate.check.disputes': 'Every dispute has an outcome',
  'gate.check.agreed': 'Farmer agreed (or closed disputed)',
  'gate.check.slipNo': 'Slip number',
  'gate.check.slipPhoto': 'Slip photo',

  // moisture (F4.4)
  'gate.moisture.required':
    'Groundnut: a reading is required. Only the supervisor can let it close without one.',
  'gate.moisture.optional': 'Optional for this crop.',
  'gate.moisture.meter': 'Meter',
  'gate.moisture.calStale': 'Calibration due',
  'gate.moisture.readingN': 'Reading {n}',
  'gate.moisture.fromBag': 'from bag {n}',
  'gate.moisture.thirdHint': 'From another good bag',
  'gate.moisture.retype': 'Type again: 3.0 to 40.0 %',
  'gate.moisture.needThird': 'Readings are more than 1.5 apart: take a third.',
  'gate.moisture.figure': 'Moisture',
  'gate.moisture.dryFirst': 'Dry before storage. Price unchanged.',
  'gate.moisture.noMeter': 'No meter today',
  'gate.moisture.noMeterSaved': 'No meter today: not read.',
  'gate.moisture.supAllows': 'Supervisor allows (no reading)',
  'gate.moisture.supReason':
    'Let this groundnut lot close without a moisture reading. It goes on hold.',
  'gate.moisture.skipped': 'Allowed by the supervisor without a reading: the lot will be on hold.',
  'gate.moisture.farmerPicked': 'farmer picked',

  // price (F4.5) and divert (F4-B1)
  'gate.price.moisture': 'Moisture',
  'gate.price.notRead': 'not read',
  'gate.price.version': 'card v{v}',
  'gate.price.deduction': 'Deduction',
  'gate.price.none': 'None this season',
  'gate.price.paidOnProduce':
    'He is paid on the weight without the sack. Moisture does not change the price this season.',
  'gate.price.noCard': 'No published card on the phone for {lot}. Update the phone.',
  'gate.price.cardHasDeductions':
    'The card for {lot} has deductions: the phone will not use it. Call the office.',
  'gate.price.hasDeductions': 'has deductions',
  'gate.price.accept': 'Accept',
  'gate.price.divert': 'Farmer will dry and come back',
  'gate.divert.title': 'Divert to drying',
  'gate.divert.choice': "Farmer's choice",
  'gate.divert.sell': 'Sell today',
  'gate.divert.dry': 'Dry at home',
  'gate.divert.reason': 'Reason',
  'gate.divert.tagsUsed': 'Tags used',
  'gate.divert.tagsFirst':
    'A tag is already on a bag: cancel as a walk-away and scan each tag void.',
  'gate.divert.card': 'Hand him the "how to dry at home" card and his bags back, untagged.',
  'gate.divert.void': 'Void draft',
  'gate.divert.back': 'Back (sell today)',

  // tag and weigh (F4.6)
  'gate.lot.good': 'Good',
  'gate.lot.red': 'Red-tie',
  'gate.lot.goodBags': 'Good bags: {count}',
  'gate.lot.redBags': 'Red-tie bags: {count}',
  'gate.lot.goodBagsShort': 'Good bags',
  'gate.lot.redBagsShort': 'Red-tie bags',
  'gate.tag.runningTotal': 'Produce so far (this lot)',
  'gate.tag.allDone': 'Every bag is tagged and weighed',
  'gate.tag.bagNofM': 'Bag {n} of {of}',
  'gate.tag.redPhase': 'Now the red-tie bags.',
  'gate.tag.typed': 'typed',
  'gate.tag.scanTag': 'Scan tag',
  'gate.tag.sackType': 'Sack type',
  'gate.tag.weight': 'Weight (kg)',
  'gate.tag.weightHint': 'As the scale shows, bag and sack together.',
  'gate.tag.newWeight': 'New weight (kg)',
  'gate.tag.produce': 'Produce weight',
  'gate.tag.saveNext': 'Save & next',
  'gate.tag.fixLast': 'Fix last bag',
  'gate.tag.fixNote': 'Both weights are kept; the new one counts.',
  'gate.tag.voidTag': 'Void tag',
  'gate.tag.bagsDone': 'Bags done',
  'gate.tag.reweighed': 're-weighed',
  'gate.tag.moreBags': '{count} {count:bag|bags} to go',
  'gate.tag.twoDecimals': 'Up to 2 decimals, as the scale shows.',
  'gate.tag.typeAgain': 'Below 5 or above 60 kg: type it again.',
  'gate.tag.notSame': 'Not the same as the first time.',
  'gate.tag.reason': 'Why',
  'gate.tag.scanToVoid': 'Scan the tag to void',
  'gate.tag.notYours': 'Not a tag of this delivery or your roll.',
  'gate.tag.keepCut': 'Keep the cut tag for the evening count.',
  'gate.tag.voided': '{code} voided',

  // sample (F4.7)
  'gate.sample.seal': 'Seal',
  'gate.sample.scanSeal': 'Scan seal',
  'gate.sample.weight': 'Sample weight (g)',
  'gate.sample.min': 'Minimum {g} g for this crop',
  'gate.sample.under': 'Under {g} g',
  'gate.sample.moreDraws': 'Take more probe draws from his good bags.',
  'gate.sample.confirmBig': 'Yes, {g} g',
  'gate.sample.photo': 'Photo',
  'gate.sample.photoHint': 'Seal number and his tagged bags in one frame.',
  'gate.sample.supAllows': 'Supervisor allows (underweight)',
  'gate.sample.supReason': 'Allow a sample of {g} g (minimum {min} g). It is reported.',
  'gate.sample.voidSeal': 'Void seal',
  'gate.sample.grams': '{g} g',
  'gate.sample.allowed': 'Allowed',

  // slip (F4.8)
  'gate.slip.bags': 'Bags',
  'gate.slip.weighed': 'Weighed',
  'gate.slip.sacks': 'Sacks',
  'gate.slip.produce': 'Produce',
  'gate.slip.deducted': 'Deducted',
  'gate.slip.payable': 'Payable weight',
  'gate.slip.price': 'Price',
  'gate.slip.value': 'Value',
  'gate.slip.total': 'Total value',
  'gate.slip.advance':
    'Any advance you owe is taken by the office from this payment; the SMS shows the amount sent.',
  'gate.slip.toBeSent': 'Will be sent to your bank; SMS will confirm.',
  'gate.slip.toBeSentHeld': 'Payment waits for a supervisor’s check; SMS will confirm.',
  'gate.slip.account': 'Account',
  'gate.slip.verified': 'verified',
  'gate.slip.noAccount':
    'No verified account: the payment will not go by itself; the office will call.',
  'gate.slip.isAccountHis': 'Is this account his?',
  'gate.slip.accountNotHis':
    'Carry on. The payment will not go by itself; the office contacts him.',
  'gate.slip.agrees': 'Farmer agrees',
  'gate.slip.disagrees': 'Farmer disagrees',
  'gate.slip.whatDisputed': 'What does he dispute?',
  'gate.slip.copyInOrder':
    'Copy onto the next carbon slip in this order, tick his storage answers, take his ink thumb on the copy.',
  'gate.slip.slipNo': 'Slip number',
  'gate.slip.slipUsed': 'This slip number was used earlier today. Type again.',
  'gate.slip.photo': 'Slip photo',
  'gate.slip.photoHint': 'The carbon copy with his ink thumb.',

  // close (F4.10) and cancel
  'gate.close.fix': 'Fix',
  'gate.close.amberWaits': 'Tell him his payment will wait for the office.',
  'gate.close.disputedHeld': 'Closed while he disputes: payment held for the supervisor.',
  'gate.close.row': 'Row',
  'gate.close.rowOne': 'Put his {n} {n:bag|bags} in a row of their own.',
  'gate.close.rowBoth':
    'Put his {good} good {good:bag|bags} in a row of their own; {red} red-tie {red:bag|bags} in a separate row.',
  'gate.close.close': 'Close delivery',
  'gate.close.done': 'Delivery closed',
  'gate.close.nextFarmer': 'Next farmer',
  'gate.cancel.button': 'Cancel delivery',
  'gate.cancel.title': 'Cancel delivery',
  'gate.cancel.explain':
    'Nothing is bought. Every tag already on a bag, and the seal, is cut and scanned void.',
  'gate.cancel.walkAway': 'He walks away / takes it back',
  'gate.cancel.diverted': 'Gone home to dry',
  'gate.cancel.movedToPaper': 'Finished on paper (tags stay on the bags)',
  'gate.cancel.scanEach': 'Cut and scan void {count} {count:code|codes}.',
  'gate.cancel.ready': 'Ready to cancel.',
  'gate.cancel.scanVoid': 'Scan a cut tag',
  'gate.cancel.notOnList': 'Not a tag of this delivery.',
  'gate.cancel.send': 'Cancel delivery',
  'gate.cancel.keep': 'Go back to the delivery',
  'gate.cancel.done': 'Delivery cancelled',

  // disputes (F4-B2)
  'gate.dispute.button': 'Dispute',
  'gate.dispute.title': 'Dispute',
  'gate.dispute.aboutLabel': 'About',
  'gate.dispute.about.WEIGHT': 'Weight',
  'gate.dispute.about.GRADE': 'Red-line call',
  'gate.dispute.about.MOISTURE': 'Moisture',
  'gate.dispute.whichBag': 'Which bag',
  'gate.dispute.whichFailed': 'Which failed bag',
  'gate.dispute.reason': 'Reason',
  'gate.dispute.record': 'Record',
  'gate.dispute.recheck': 'Re-check',
  'gate.dispute.firstValue': 'First value',
  'gate.dispute.newWeight': 'New weight (kg)',
  'gate.dispute.sameBag': 'Same bag, same scale, farmer watching.',
  'gate.dispute.testWeight': 'Test weight reading ({kg})',
  'gate.dispute.testWeightHint': 'Only if he says the scale is wrong.',
  'gate.dispute.testOff':
    'Scale is off by more than 0.1 kg: stop weighing and call the supervisor.',
  'gate.dispute.gradeHint': 'Hold the bag against the red-line card with him and take a new photo.',
  'gate.dispute.photo': 'Photo',
  'gate.dispute.farmerPicked': 'Farmer picked the bags',
  'gate.dispute.outcome': 'Outcome',
  'gate.dispute.allValues': 'All values',
  'gate.dispute.nowCounts': 'Now counts',
  'gate.dispute.money': 'Effect on money',
  'gate.dispute.callStands': 'The red-line call stands',
  'gate.dispute.redTieStays': 'The red tie stays on: a call is never reversed at the gate.',
  'gate.dispute.settled': 'Settled',
  'gate.dispute.bagHome': 'Bag goes home',
  'gate.dispute.closeHeld': 'Close, payment after check',

  // deliveries list (F4.9-l)
  'gate.list.title': "Today's deliveries",
  'gate.list.hint': 'Lots, holds and payment status',
  'gate.list.day': 'Day',
  'gate.list.empty': 'No deliveries yet.',
  'gate.list.inProgress': 'In progress',
  'gate.list.waiting': 'Waiting to send',
  'gate.list.sending': 'Sending',
  'gate.list.received': 'Received',
  'gate.list.held': 'On hold at office',
  'gate.list.problem': 'Needs attention',
  'gate.list.onHold': 'On hold',
  'gate.list.disputed': 'Disputed',
  'gate.list.slip': 'slip {no}',
  'gate.pay.waiting': 'Waiting',
  'gate.pay.sent': 'Sent to bank',
  'gate.pay.paid': 'Paid',
  'gate.pay.officeWillCall': 'Office will call',

  // close day (F4.11)
  'gate.closeDay.draftOpen': 'Finish or cancel the delivery of {name} first.',
  'gate.closeDay.lotsToday': 'Lots today',
  'gate.closeDay.lotsLine': '{lots} {lots:lot|lots} · {bags} {bags:bag|bags} · {kg}',
  'gate.closeDay.tagCount': 'Tag count',
  'gate.closeDay.sealCount': 'Seal count',
  'gate.closeDay.inHand': 'In hand',
  'gate.closeDay.plusOpened': 'incl. {n} opened',
  'gate.closeDay.onBags': 'On bags',
  'gate.closeDay.voided': 'Voided',
  'gate.closeDay.shouldBeLeft': 'Should be left',
  'gate.closeDay.tagsCounted': 'Unused tags (counted)',
  'gate.closeDay.sealsCounted': 'Unused seals (counted)',
  'gate.closeDay.balanced': 'Balanced',
  'gate.closeDay.notBalanced':
    'Does not balance. The day closes with an exception; the supervisor resolves it.',
  'gate.closeDay.recordsWaiting': 'Records waiting: {count}',
  'gate.closeDay.photosWaiting': 'Photos waiting: {count}',
  'gate.closeDay.close': 'Close day',
  'gate.closeDay.closed': 'Day closed. Keep the phone on network until all records are sent.',
  'gate.closeDay.waitingServer':
    'Closed on the phone. Waiting for the records to reach the office.',
  'gate.closeDay.serverBalanced': 'The office balanced the day.',
  'gate.closeDay.serverUnbalanced': 'The day did not balance at the office.',
  'gate.closeDay.allSent': 'All records sent. The office reconciles the day.',

  // paper day (F4-B3)
  'gate.paper.title': 'Type in a paper day',
  'gate.paper.hint': 'Back-entry of receipt-book slips within 24 h',
  'gate.paper.short': 'Paper day',
  'gate.paper.continue': 'Continue the paper day',
  'gate.paper.explain':
    'Type in the paper day from the receipt book within 24 hours, where the bags are. Every lot is held until a supervisor checks it.',
  'gate.paper.cover': 'Cover page',
  'gate.paper.operator': 'Paper-day operator',
  'gate.paper.book': 'Receipt book',
  'gate.paper.bookNo': 'Book',
  'gate.paper.noBook': 'No receipt book issued to this operator.',
  'gate.paper.startTime': 'Start time',
  'gate.paper.endTime': 'End time',
  'gate.paper.testRead': 'Scale showed for the test weight ({kg})',
  'gate.paper.testOff': 'More than 0.1 kg off: the day is flagged for the review.',
  'gate.paper.rollHint': 'If the roll was not confirmed on the day, scan its first and last tag.',
  'gate.paper.open': 'Start typing in',
  'gate.paper.opened': 'Paper day opened',
  'gate.paper.typedSlips': '{n} typed in',
  'gate.paper.spoiledSlips': '{n} spoiled',
  'gate.paper.missing': 'Missing slips: {slips}. Type them in or mark them spoiled.',
  'gate.paper.typeSlip': 'Type in a slip',
  'gate.paper.spoiled': 'Slip was spoiled',
  'gate.paper.spoiledSaved': 'Slip {slip} marked spoiled',
  'gate.paper.close': 'Close paper day',
  'gate.paper.slipTime': 'Time on slip',
  'gate.paper.pagePhoto': 'Page photo',
  'gate.paper.notInBook': 'Not in this book ({from}–{to}).',
  'gate.paper.slipUsed': 'This slip is already typed in or spoiled.',
  'gate.paper.typingSlip': 'Slip {slip} of book {book}: find the farmer by roster number and name.',
  'gate.paper.slipEyebrow': 'Paper slip {slip}',
  'gate.paper.backToDay': 'Back to the paper day',

  // re-tag (F4.6-h)
  'gate.retag.title': 'Re-tag held bags',
  'gate.retag.hint': 'Supervisor: new tag for a bag kept without one',
  'gate.retag.supOnly': 'Only a supervisor can re-tag (his PIN is asked).',
  'gate.retag.none': 'Nothing waits for a tag.',
  'gate.retag.noneHint': 'Bags and samples the office kept without a tag show here after sync.',
  'gate.retag.bag': 'Bag',
  'gate.retag.sample': 'Sample',
  'gate.retag.conflict': 'Scanned code {code} was already on another bag.',
  'gate.retag.scanTag': 'Scan new tag',
  'gate.retag.scanSeal': 'Scan new seal',
  'gate.retag.supReason': 'Bind a new tag to this held bag or sample.',
  'gate.retag.done': 'New tag {code} saved',

  // codes from the roster / server (fallback labels)
  'gate.code.MOULD': 'Mould',
  'gate.code.SPROUTING': 'Sprouting',
  'gate.code.INSECT_DAMAGE': 'Insect damage',
  'gate.code.FOREIGN_MATTER': 'Foreign matter',
  'gate.code.CHEMICAL_SACK': 'Reused chemical sack',
  'gate.code.OTHER': 'Other',
  'gate.code.TORN': 'Torn',
  'gate.code.MISPRINT': 'Misprint',
  'gate.code.LOOKS_WET': 'Looks wet',
  'gate.code.WALK_AWAY': 'Walked away',
  'gate.code.WRONG_FARMER': 'Wrong farmer',
  'gate.code.DUPLICATE': 'Opened twice',
  'gate.code.DIVERTED_TO_DRY': 'Gone home to dry',
  'gate.code.MOVED_TO_PAPER': 'Moved to paper',
  'gate.code.TYPED_DIFFERS': 'Typed number differs from scale',
  'gate.code.SCALE_NOT_SETTLED': 'Scale had not settled',
  'gate.code.SCALE_WRONG': 'Scale is wrong',
  'gate.code.DEFECT_NOT_THERE': 'Defect not there',
  'gate.code.WRONG_LINE': 'Wrong line on the card',
  'gate.code.READING_TOO_HIGH': 'Reading too high',
  'gate.code.STORAGE_RISK': 'Storage answer',
  'gate.code.OVER_ESTIMATE': 'Over field estimate',
  'gate.code.TAG_CONFLICT': 'Tag on two bags',
  'gate.code.SAMPLE_MISSING': 'Sample missing',
  'gate.code.MOISTURE_NOT_READ': 'Moisture not read',
  'gate.code.BAG_COUNT_MISMATCH': 'Bag count',
  'gate.code.PAPER_DAY': 'Paper day',
  'gate.code.DEVICE_REVOKED': 'Phone revoked',
  'gate.code.ORIGIN_MISMATCH': 'Origin check',
  'gate.code.OPEN_DISPUTE': 'Open dispute',
  'gate.crop.groundnut': 'Groundnut',
  'gate.crop.moong': 'Moong',
  'gate.crop.sesame': 'Sesame',

  // V1 · Seva Mandir (roster config.scope): the alignment map's V1 gate boards. ** = bold.
  'gate.v1.when.today': 'Today {time}',
  'gate.v1.when.yesterday': 'Yesterday {time}',
  'gate.v1.a11y.speakQuestion': 'Read the question aloud',
  'gate.v1.a11y.speak': 'Read it aloud',
  'gate.v1.a11y.farmerPhoto': "Farmer's photo",
  'gate.v1.a11y.registeredPhoto': 'Photo from registration',
  'gate.v1.a11y.photo': 'Photo',
  'gate.v1.a11y.pending': '{count} still to send',
  'gate.v1.a11y.allSent': 'All sent',
  'gate.v1.a11y.moreOptions': 'More options',
  'gate.v1.a11y.changeSack': 'Change sack type',
  'gate.v1.camera.torch': 'Torch',
  'gate.v1.unit.kg': 'kg',
  'gate.v1.spoken.kg': '{value} kg',
  'gate.v1.spoken.percent': '{value} percent',
  'gate.v1.help.photo1': 'Photo: open bag and card',
  'gate.v1.help.photo2': 'Photo: hands drawing the sample',
  'gate.v1.help.photo3': 'Photo: hands tying the tag',
  'gate.v1.help.photo4': 'Photo: the scale display',
  'gate.v1.help': 'Help',
  'gate.v1.more.weighHelp': 'How to weigh and tag a bag',
  'gate.v1.update.a11y': 'App version {version}. Update this phone',
  'gate.v1.update.title': 'Update this phone',
  'gate.v1.update.version': 'App version {version}',
  'gate.v1.update.what':
    'Sends waiting work, downloads the latest farmer list and settings, and installs a new app version if there is one.',
  'gate.v1.update.now': 'Update now',
  'gate.v1.update.busy': 'Updating…',
  'gate.v1.update.done': 'Phone updated. App is the latest version.',
  'gate.v1.update.reloading': 'New version found. Restarting the app…',
  'gate.v1.update.offline': 'No internet. Connect and tap Update again.',
  'gate.v1.update.signedOut': 'Sign in to download the latest farmer list.',
  'gate.v1.update.failed': 'Update failed: {error}',
  'gate.v1.update.locked': 'Finish this work first. Then update from the Home screen.',
  'gate.v1.update.waiting': 'A new app version is ready. It installs when you update from Home.',
  'gate.v1.update.demo': 'This is a demo phone. It cannot download from the server.',
  'gate.v1.back': 'Back',
  'gate.v1.pending': '{count} still to send',
  'gate.v1.allSent': 'All sent',
  'gate.v1.farmerNo': 'No. {no}',
  'gate.v1.farmerLine': 'No. {no} · {crop}',
  'gate.v1.father': 'Father: {name}',
  'gate.v1.kg': '{kg} kg',
  'gate.v1.bags': '{count} {count:bag|bags}',
  'gate.v1.next': 'Next',
  'gate.v1.save': 'Save',
  'gate.v1.change': 'Change',
  'gate.v1.fix': 'Fix',
  'gate.v1.why': 'Why?',
  'gate.v1.pickReason': 'Pick a reason',
  'gate.v1.listen': 'Listen',
  'gate.v1.answer.NO': 'No',
  'gate.v1.answer.DONT_KNOW': 'Don’t know',
  'gate.v1.answer.YES': 'Yes',
  'gate.v1.grade.good': 'Good',
  'gate.v1.grade.red': 'Red tie',
  'gate.v1.lot.good': 'Good',
  'gate.v1.lot.red': 'Red tie',
  'gate.v1.perKg': '{price} per kg',
  'gate.v1.saveFailed': 'Not saved',
  'gate.v1.find.title': 'Find the farmer',
  'gate.v1.bank.title': 'Bank account',
  'gate.v1.village.title': 'Which village today?',
  'gate.v1.village.line': '{name}, you work in more than one village. Pick the one you are in now.',
  'gate.v1.village.tap': 'Tap the village',
  'gate.v1.village.current': 'This phone is set to this village',
  'gate.v1.village.switch': 'Switch the phone to this village',
  'gate.v1.village.loading': 'Downloading its farmer list…',
  'gate.v1.village.needNet': 'Switching village downloads its farmer list: it needs the internet.',
  'gate.v1.village.closeFirst':
    'A day or a purchase is still open in {village} on this phone. Finish it and close the day first, then switch.',
  'gate.v1.village.change': 'Change village',
  'gate.v1.bank.replaceChecked':
    '{name} already has account ••••{last4} (checked). Add a new one only to change it: it replaces ••••{last4} once the office approves it. Until then, payments still go to ••••{last4}.',
  'gate.v1.bank.replacePending':
    "{name}'s account ••••{last4} is already added and waiting for the office's approval. Add a new one only if ••••{last4} is wrong.",
  'gate.v1.bank.replaceQueued':
    "{name}'s account ••••{last4} is saved on this phone and will be checked when online. Add a new one only if ••••{last4} is wrong.",
  'gate.v1.bank.offlineQueue':
    'No network: the account number can be saved securely on this phone and verified through OnGrid when the phone is back online.',
  'gate.v1.bank.numberLineOffline':
    'From their passbook. Saved on this phone now, verified through OnGrid when online.',
  'gate.v1.bank.saveQueued': 'Save on phone · check when online',
  'gate.v1.bank.queuedAccount': 'Account ••••{last4} · saved on phone',
  'gate.v1.bank.queuedLine':
    'It goes to OnGrid when the phone is online; then the office approves it. If verification fails, the office will call.',
  'gate.v1.bank.hasQueued': 'Account ••••{last4} · saved on phone, not checked yet',
  'gate.v1.bank.why':
    "{name}'s money goes to their own bank account. Add it now: it takes a minute.",
  'gate.v1.bank.offline':
    'No network: the bank check needs the internet. Do it later from Home · Collect account details.',
  'gate.v1.bank.upi': 'UPI · they pay ₹1',
  'gate.v1.bank.upiLine':
    'From their own UPI app (PhonePe, GPay, Paytm…). The bank tells us the account.',
  'gate.v1.bank.number': 'Account number',
  'gate.v1.bank.numberLine': 'From their passbook. OnGrid verifies the account and IFSC.',
  'gate.v1.bank.accountNo': 'Account number',
  'gate.v1.bank.accountNoAgain': 'Account number again',
  'gate.v1.bank.notSame': 'The two numbers are not the same',
  'gate.v1.bank.ifsc': 'IFSC (on the passbook)',
  'gate.v1.bank.ifscBad': 'IFSC is 11 letters and digits, e.g. SBIN0001234',
  'gate.v1.bank.nameOnPassbook': 'Name on the passbook',
  'gate.v1.bank.numberNotKept':
    'The verified full number is encrypted on the server. Screens show only the last 4 digits.',
  'gate.v1.bank.consent':
    'The farmer agrees that these bank-account details may be verified through OnGrid.',
  'gate.v1.bank.verify': 'Verify and save account',
  'gate.v1.bank.send1': 'Send ₹1 to check',
  'gate.v1.bank.fillAll': 'Type the number twice, the IFSC, and confirm consent',
  'gate.v1.bank.upiAsk': '{name} scans this with their UPI app and pays ₹1 from their own account.',
  'gate.v1.bank.upiAskLink':
    "Send this UPI link to {name}'s phone. They open it and pay ₹1 from their own account — not from this phone.",
  'gate.v1.bank.shareLink': 'Send link to their phone',
  'gate.v1.bank.qr': 'UPI QR to pay ₹1',
  'gate.v1.bank.openUpi': 'Open in a UPI app',
  'gate.v1.bank.waitingUpi': 'Waiting for their ₹1…',
  'gate.v1.bank.waitingBank': 'The bank is checking the account…',
  'gate.v1.bank.failedBank':
    'OnGrid could not verify this account. Check the account number and IFSC.',
  'gate.v1.bank.doneAccount': 'Account ••••{last4} · verified',
  'gate.v1.bank.onGridResult': 'OnGrid result: {status}',
  'gate.v1.bank.nameAtBank': 'Name at the bank: {name}',
  'gate.v1.bank.nameNotReturned': 'OnGrid did not return an account-holder name.',
  'gate.v1.bank.bankName': 'Bank: {bank}',
  'gate.v1.bank.confirmResult':
    'Show these OnGrid details to the farmer and confirm the name, bank, IFSC and last 4 digits.',
  'gate.v1.bank.officeApproves': 'The office approves it, then their payments go to this account.',
  'gate.v1.bank.tryAgain': 'Try again',
  'gate.v1.bank.later': 'Not now · payment waits',
  'gate.v1.slip.queuedAccount': 'Account ••••{last4} · saved, checked when the phone is online',
  'gate.v1.slip.pendingAccount': 'Account ••••{last4} · being approved by the office',
  'gate.v1.home.collectBank': 'Collect account details',
  'gate.v1.home.collectBankLine': 'Bank account',
  'gate.v1.bank.pick': 'Whose bank account?',
  'gate.v1.bank.hasVerified': 'Account ••••{last4} · checked',
  'gate.v1.bank.hasPending': 'Account ••••{last4} · being approved',
  'gate.v1.bank.hasNone': 'No bank account',
  'gate.v1.bank.addAnother': 'Add another account',
  'gate.v1.bank.saved': 'Saved. The office approves it next.',
  'gate.v1.confirm.takePhoto': 'Take his photo',
  'gate.v1.confirm.retakePhoto': 'Take his photo again',
  'gate.v1.confirm.noPhoto': 'No photo on record · if you want',
  'gate.v1.find.rosterNo': 'Farmer ID number (e.g. U001 → 1)',
  'gate.v1.find.found': 'Found',
  'gate.v1.find.none': 'No farmer with this name. Check the spelling, or search by father’s name.',
  'gate.v1.find.byName': 'Search by name',
  'gate.v1.find.name': 'Name, father’s name or farmer ID',
  'gate.v1.find.clear': 'Clear',
  'gate.v1.find.tapName': 'Tap his name',
  'gate.v1.find.matches': '{n} found',
  'gate.v1.find.inVillage': '{n} farmers in {village}',
  'gate.v1.find.showAll': 'Show all ({n} more)',
  'gate.v1.find.notListed': 'Not on the list',
  'gate.v1.find.notListedBody': 'Not on the list: no purchase today. Tell the supervisor.',
  'gate.v1.find.cta': 'This farmer',
  'gate.v1.find.typeNo': 'Type the roster number',
  'gate.v1.confirm.title': 'Is this him?',
  'gate.v1.confirm.face': 'Match the face',
  'gate.v1.confirm.no': 'No, not him',
  'gate.v1.confirm.yes': 'Yes, it is him',
  'gate.v1.crop.acre': 'acre',
  'gate.v1.crop.hectare': 'hectare',
  'gate.v1.crop.sown': 'sown {date}',
  'gate.v1.crop.title': 'Field and crop',
  'gate.v1.crop.twoCrops': 'Two fields? One purchase each',
  'gate.v1.crop.none':
    'None of his crops is marked ready for harvest. Do not buy today — call the supervisor',
  'gate.v1.storage.title': 'Storage',
  'gate.v1.storage.q1': 'Kept at home over a week?',
  'gate.v1.storage.q2': 'Any chemical put in?',
  'gate.v1.storage.q2hint': 'Sulphas, tablet, powder · neem or ash = No',
  'gate.v1.storage.left': '{count} {count:answer|answers} left',
  'gate.v1.storage.q1short': 'Kept over a week',
  'gate.v1.storage.q2short': 'Chemical put in',
  'gate.v1.dry.button': 'Dry at home',
  'gate.v1.dry.title': 'Dry at home',
  'gate.v1.dry.later': 'Bring it later',
  'gate.v1.dry.price': 'At that day’s price',
  'gate.v1.dry.card': 'Give the **drying card**',
  'gate.v1.dry.bags': 'Return the bags **untagged**',
  'gate.v1.dry.sellToday': 'Sell today',
  'gate.v1.dry.confirm': 'Confirm',
  'gate.v1.moisture.title': 'Moisture reading',
  'gate.v1.moisture.meterSet': 'Meter set to **{crop}**',
  'gate.v1.moisture.anyBag': 'From any one good bag',
  'gate.v1.moisture.noMeter': 'No meter today',
  'gate.v1.moisture.noMeterSaved': 'No meter today · the office is told',
  'gate.v1.moisture.range': 'Type between {min}% and {max}%',
  'gate.v1.moisture.type': 'Type the reading',
  'gate.v1.tag.title': 'Tag the bags',
  'gate.v1.tag.sub': 'Tag bag {n}',
  'gate.v1.tag.lastSaved': 'Bag {n} saved · {kg} kg',
  'gate.v1.tag.hold': 'Hold the tag up',
  'gate.v1.tag.type': 'Type the number',
  'gate.v1.tag.typeTitle': 'Type the tag number',
  'gate.v1.tag.fixLast': 'Fix last',
  'gate.v1.tag.allDone': 'All bags done',
  'gate.v1.tag.noneYet': 'Tag the first bag',
  'gate.v1.tag.camera': 'Camera did not open · type the number',
  'gate.v1.tag.voidTag': 'Spoiled tag',
  'gate.v1.weigh.title': 'Weight',
  'gate.v1.weigh.paperSub': 'Weight from the paper slip',
  'gate.v1.weigh.bandGood': 'Good · {count} {count:bag|bags} · {kg} kg',
  'gate.v1.weigh.bandRed': 'Red tie · {count} {count:bag|bags} · {kg} kg',
  'gate.v1.weigh.tag': 'Tag {code}',
  'gate.v1.weigh.weightOf': 'Weight of bag {n} (with sack)',
  'gate.v1.weigh.paperWeight': 'Weight on the slip',
  'gate.v1.weigh.net': 'Produce',
  'gate.v1.weigh.scalePhoto': 'Photo of the scale',
  'gate.v1.weigh.scalePhotoTaken': 'Scale photo taken',
  'gate.v1.weigh.scalePhotoRetake': 'Take the scale photo again',
  'gate.v1.weigh.optional': 'if you want',
  'gate.v1.moisture.photo': 'Photo of the meter',
  'gate.v1.moisture.photoTaken': 'Meter photo taken',
  'gate.v1.moisture.photoRetake': 'Take the meter photo again',
  'gate.v1.weigh.saveGood': 'Probe drawn, save',
  'gate.v1.weigh.saveRed': 'Red tie on, save',
  'gate.v1.weigh.savePaper': 'Save, next bag',
  'gate.v1.weigh.pickGrade': 'Good or red tie?',
  'gate.v1.weigh.pickSack': 'Pick the sack',
  'gate.v1.weigh.typeWeight': 'Type the weight',
  'gate.v1.weigh.notMoreThanSack': 'Weight must be more than the sack',
  'gate.v1.weigh.again': 'Outside {min}–{max} kg · type it again',
  'gate.v1.weigh.sackTitle': 'Sack',
  'gate.v1.weigh.sackLine': '{kg} kg',
  'gate.v1.weigh.sackLetter': 'Sack {letter}',
  'gate.v1.weigh.seePage': 'See the page',
  'gate.v1.fix.title': 'Fix the last bag?',
  'gate.v1.fix.bag': 'Bag {n} · tag {code}',
  'gate.v1.fix.was': 'Was typed',
  'gate.v1.fix.typo': 'Typed wrong',
  'gate.v1.fix.reweighed': 'Weighed again',
  'gate.v1.fix.warn': 'After saving it **cannot change**',
  'gate.v1.fix.newWeight': 'New weight (with sack)',
  'gate.v1.help.weighTitle': 'Weighing: how to',
  'gate.v1.help.step1': 'Open the bag, match it to the card',
  'gate.v1.help.step2': 'Good: draw the sample · failed: red tie',
  'gate.v1.help.step3': 'Tie the tag, scan it',
  'gate.v1.help.step4': 'Let the number settle, type that',
  'gate.v1.help.trouble': 'If something goes wrong',
  'gate.v1.help.tip1': 'Won’t scan → **type the number**',
  'gate.v1.help.tip2': 'Wrong weight saved → **fix last**',
  'gate.v1.help.tip3': 'His own sack → change the **sack**',
  'gate.v1.help.gotIt': 'Got it',
  'gate.v1.help.generalTitle': 'Help',
  'gate.v1.help.general':
    'If anything is unclear, phone the supervisor. What is saved stays on the phone.',
  'gate.v1.pin.who': 'Who is working?',
  'gate.v1.pin.whoSub': 'Pick your name',
  'gate.v1.pin.title': 'Enter PIN',
  'gate.v1.pin.sub': 'Sign in with your PIN',
  'gate.v1.pin.press': 'Press your {n}-digit PIN',
  'gate.v1.pin.dots': '{done} of {n} digits pressed',
  'gate.v1.pin.forgot': 'Forgot PIN',
  'gate.v1.pin.forgotText':
    'Ask your supervisor. They set a new PIN from the office; it reaches this phone with the next list.',
  'gate.v1.pin.open': 'Sign in',
  'gate.v1.pin.role.village_operator': 'purchase operator',
  'gate.v1.pin.role.supervisor': 'supervisor',
  'gate.v1.seal.title': 'Seal, scan',
  'gate.v1.seal.inFront': '**In front of the farmer**, close the bag',
  'gate.v1.seal.hold': 'Hold the seal up',
  'gate.v1.seal.code': 'Seal {code}',
  'gate.v1.seal.scanned': 'Scanned',
  'gate.v1.seal.save': 'Save, into the box',
  'gate.v1.refuse.unknown': 'Not from this village’s rolls. Keep it aside, take the next.',
  'gate.v1.refuse.otherFarmer': 'This is on {name}’s bag. Open {name} from Home.',
  'gate.v1.seal.typeTitle': 'Type the seal number',
  'gate.v1.slip.title': 'Your account',
  'gate.v1.slip.handBack': 'Take the phone back',
  'gate.v1.slip.value': 'Value of produce',
  'gate.v1.slip.account': 'Account ••••{last4} · checked',
  'gate.v1.slip.isYours': 'Is it yours?',
  'gate.v1.slip.noAccount': 'Bank account not checked · the office will call',
  'gate.v1.slip.parts': 'Paid **in {count} parts**',
  'gate.v1.slip.bank': 'Sent to your bank · an SMS will come',
  'gate.v1.slip.otp': 'Never share an OTP',
  'gate.v1.slip.goodLot': 'Good · {count} {count:bag|bags}',
  'gate.v1.slip.redLot': 'Red tie · {count} {count:bag|bags}',
  'gate.v1.slip.show': 'Show',
  'gate.v1.slip.hide': 'Hide',
  'gate.v1.slip.bagLine': 'Tag {tag}',
  'gate.v1.slip.lot': 'Lot {code}',
  'gate.v1.slip.sack': 'Sacks',
  'gate.v1.slip.less': '{kg} kg less',
  'gate.v1.slip.net': 'Produce weight',
  'gate.v1.slip.price': 'Price',
  'gate.v1.slip.priceLine': '{kg} per kg ({q} per quintal)',
  'gate.v1.slip.amount': 'Amount',
  'gate.v1.slip.agrees': 'Farmer agrees',
  'gate.v1.paperSlip.title': 'Paper slip',
  'gate.v1.paperSlip.number': 'Printed no. {no}',
  'gate.v1.paperSlip.typeNo': 'Type the printed number',
  'gate.v1.paperSlip.used': 'Already used today',
  'gate.v1.paperSlip.farmer': 'Farmer',
  'gate.v1.paperSlip.bags': 'Bags',
  'gate.v1.paperSlip.kg': 'Kg',
  'gate.v1.paperSlip.amount': 'Amount',
  'gate.v1.paperSlip.answers': '2 questions',
  'gate.v1.paperSlip.thumb': '2. Thumb on the copy below',
  'gate.v1.paperSlip.photoTaken': 'Photo taken',
  'gate.v1.paperSlip.takePhoto': 'Take photo',
  'gate.v1.paperSlip.again': 'Again',
  'gate.v1.paperSlip.next': 'Next',
  'gate.v1.final.title': 'Final check',
  'gate.v1.final.missing': '{count} {count:check|checks} missing',
  'gate.v1.final.tags': 'Tags',
  'gate.v1.final.sealed': 'Sample sealed',
  'gate.v1.final.storage': '2 questions',
  'gate.v1.final.moisture': 'Moisture',
  'gate.v1.final.cards': 'Price',
  'gate.v1.final.slipNo': 'Slip no.',
  'gate.v1.final.slipPhoto': 'Slip photo',
  'gate.v1.final.agreed': 'Agreed',
  'gate.v1.final.left': '{name} missing',
  'gate.v1.final.rows': '**Own row** · **slate** in front',
  'gate.v1.final.rowsLabel':
    'Each lot in its own row with the lot-number slate in front; never in a heap',
  'gate.v1.final.close': 'Close delivery',
  'gate.v1.final.more': 'More',
  'gate.v1.confirm2.title': 'Close the purchase?',
  'gate.v1.confirm2.warn':
    'Once closed it **cannot change**. A mistake is fixed by the supervisor.',
  'gate.v1.confirm2.yes': 'Yes, close',
  'gate.v1.done.title': 'Purchase closed',
  'gate.v1.done.value': 'Value {amount}',
  'gate.v1.done.next': 'Next farmer',
  'gate.v1.done.cancelled': 'Purchase cancelled',
  'gate.v1.done.nothingBought': 'Nothing bought',
  'gate.v1.done.home': 'Home',
  'gate.v1.cancel.title': 'Cancel the purchase?',
  'gate.v1.cancel.nothing': 'Nothing will be bought',
  'gate.v1.cancel.cutTags': 'Cut off the {count} {count:tag|tags} on the bags',
  'gate.v1.cancel.left': 'Farmer left',
  'gate.v1.cancel.dry': 'Dry at home',
  'gate.v1.cancel.cutAndCancel': 'Tags cut, cancel',
  'gate.v1.cancel.cancelNow': 'Cancel',
  'gate.v1.open.title': 'Open day',
  'gate.v1.open.listFrom': 'List: {when}',
  'gate.v1.open.setupFirst': 'Set up first',
  'gate.v1.open.scale': 'Scale',
  'gate.v1.open.rateChart': 'Rate chart',
  'gate.v1.open.redLine': 'Red-line card',
  'gate.v1.open.roster': 'List',
  'gate.v1.open.rates': 'Price · {crop}',
  'gate.v1.open.hasDeductions': 'Card has deductions: ask the office',
  'gate.v1.open.cta': 'Open day',
  'gate.v1.open.noRolls': 'No tag roll or seal strip for this village. Ask the office.',
  'gate.v1.open.noCard': 'No price for today. Ask the office.',
  'gate.v1.open.listOld': 'The list is old. Get it again on network.',
  'gate.v1.home.title': 'Today · {date}',
  'gate.v1.home.sub': 'Today',
  'gate.v1.home.dayOpen': 'Day open',
  'gate.v1.home.dayOpenLine': '{village} · {time}',
  'gate.v1.home.dayClosed': 'Day not open',
  'gate.v1.home.battery': 'Battery {pct}% · space OK',
  'gate.v1.home.farmers': 'Farmers',
  'gate.v1.home.bags': 'Bags',
  'gate.v1.home.kg': 'Kg',
  'gate.v1.home.paper': 'Type in paper',
  'gate.v1.home.office': 'Office',
  'gate.v1.home.closeDay': 'Close day',
  'gate.v1.home.deliveries': 'Today’s purchases',
  'gate.v1.home.nextFarmer': 'Next farmer',
  'gate.v1.toHome': 'Home: keep this farmer open',
  'gate.v1.task.title': 'Tasks',
  'gate.v1.task.screen': 'This purchase',
  'gate.v1.task.forms': 'Storage questions',
  'gate.v1.task.moisture': 'Moisture',
  'gate.v1.task.bags': 'Bags: tag and weigh',
  'gate.v1.task.seal': 'Sample seal',
  'gate.v1.task.bank': 'Bank account',
  'gate.v1.task.short.forms': 'Forms',
  'gate.v1.task.short.moisture': 'Moisture',
  'gate.v1.task.short.bags': 'Bags',
  'gate.v1.task.short.seal': 'Seal',
  'gate.v1.task.short.bank': 'Bank',
  'gate.v1.task.done': 'Done',
  'gate.v1.task.toDo': 'To do',
  'gate.v1.task.optional': 'Optional',
  'gate.v1.task.weighLeft': 'weigh every bag',
  'gate.v1.task.sealNotNeeded': 'Not needed: every bag has a red tie',
  'gate.v1.task.showSlip': 'Show slip',
  'gate.v1.task.locked': 'Slip agreed: no more changes',
  'gate.v1.home.left': 'Left: {tasks}',
  'gate.v1.home.readySlip': 'Ready for slip',
  'gate.v1.moisture.change': 'Change',
  'gate.v1.sack.other': '+ Other sack',
  'gate.v1.sack.typed': 'typed at the gate',
  'gate.v1.sack.otherLine': 'Not in the list: type its name and weight',
  'gate.v1.sack.needList': 'Update the farmer list (on network) to use this',
  'gate.v1.sack.otherTitle': 'Other sack',
  'gate.v1.sack.name': 'Sack name',
  'gate.v1.sack.weight': 'Empty sack weight (grams, 0 if none)',
  'gate.v1.sack.fill': 'Type the name and weight',
  'gate.v1.sack.tooHeavy': 'An empty sack weighs at most {g} grams',
  'gate.v1.sack.waits': 'His payment waits for the office to approve this sack weight.',
  'gate.v1.tag.seeAll': 'See all',
  'gate.v1.tag.allTitle': 'All bags · {count}',
  'gate.v1.tag.tagNo': 'Tag',
  'gate.v1.tag.grossLine': 'on scale {kg} kg',
  'gate.v1.tag.withdrawn': '{count} {count:bag|bags} went home in a dispute',
  'gate.v1.home.alreadyOpen': '{name} is already open: carrying on with him',
  'gate.v1.home.deleteFarmer': 'Delete {name}',
  'gate.v1.delete.title': 'Delete {name}?',
  'gate.v1.delete.voidCodes': 'Tags and seal will be voided. Cut them off the bags.',
  'gate.v1.delete.wrongFarmer': 'Wrong farmer',
  'gate.v1.delete.duplicate': 'Opened twice',
  'gate.v1.delete.confirm': 'Delete',
  'gate.v1.home.openDay': 'Open day',
  'gate.v1.closeDay.title': 'Close day',
  'gate.v1.closeDay.lots': 'Lots today',
  'gate.v1.closeDay.goodLots': 'Good · {count} {count:lot|lots}',
  'gate.v1.closeDay.redLots': 'Red tie · {count} {count:lot|lots}',
  'gate.v1.closeDay.lotLine': '{bags} {bags:bag|bags} · {kg} kg',
  'gate.v1.closeDay.toSend': 'Still to send',
  'gate.v1.closeDay.pending': '{count} {count:record|records} · {photos} {photos:photo|photos}',
  'gate.v1.closeDay.sendNow': 'Send now',
  'gate.v1.closeDay.keepNetwork': 'Keep network on until **All sent**',
  'gate.v1.closeDay.draftOpen': 'First finish {name}’s purchase',
  'gate.v1.closeDay.draftsOpen': 'First finish the {count} open farmers',
  'gate.v1.closeDay.closed': 'Day closed',
  'gate.v1.closeDay.allSentDone': 'All sent',
  'gate.v1.paper.title': 'Paper day',
  'gate.v1.paper.typist': 'Office · own PIN',
  'gate.v1.paper.day': '**{village}** · {date} · within 24 h',
  'gate.v1.paper.typed': '{count} {count:slip|slips} typed in',
  'gate.v1.paper.pagePhoto': 'Page photo',
  'gate.v1.paper.taken': 'Taken',
  'gate.v1.paper.scanEach': '**Scan the tag** of every bag',
  'gate.v1.paper.typeSlip': 'Type in slip {no}',
  'gate.v1.paper.slipNo': 'Slip number',
  'gate.v1.paper.slipTime': 'Time on the slip',
  'gate.v1.paper.pickDay': 'Slips of which day?',
  'gate.v1.paper.open': 'Open the paper day',
  'gate.v1.paper.band': 'Paper · slip {no}',
  'gate.v1.paper.close': 'Close the paper day',
  'gate.v1.home.load': 'Load vehicle',
  'gate.v1.home.loadLine': '{lots} {lots:lot|lots} · {bags} {bags:bag|bags}',
  'gate.v1.home.pay': 'Payments',
  'gate.v1.home.batteryLow': 'Battery {pct}% · space low',
  'gate.v1.home.spaceOk': 'Space OK',
  'gate.v1.home.spaceLow': 'Space low',
  'gate.v1.pay.title': 'Payments',
  'gate.v1.pay.search': 'Roster no.',
  'gate.v1.pay.paid': 'Reached',
  'gate.v1.pay.sent': 'Sent to the bank',
  'gate.v1.pay.waiting': 'After the office check',
  'gate.v1.pay.problem': 'The office will call',
  'gate.v1.pay.notSent': 'On the phone · not sent yet',
  'gate.v1.pay.none': 'No purchases',
  'gate.v1.pay.home': 'Home',
  'gate.v1.sync.failed': 'Could not send · check the network',
  'gate.v1.sync.last': 'Last sent: {when}',
  'gate.v1.sync.offlineOk':
    'Everything is safe on the phone. It goes by itself when there is network.',
  'gate.v1.camera.denied': 'Camera permission is off · allow the camera in the browser',
  'gate.v1.camera.none': 'No camera found · type the number',
  'gate.v1.camera.insecure': 'The camera needs an https address',
  'gate.v1.camera.retry': 'Try again',
  'gate.v1.photo.title': 'Take a photo',
  'gate.v1.photo.shoot': 'Take the photo',
  'gate.v1.photo.gallery': 'Pick from the phone',
  'gate.v1.photo.saving': 'Saving the photo',
  'gate.check.bagsTagged': 'Tags and weights ({count} {count:bag|bags})',
  'gate.check.sealed': 'Sample sealed',
  'gate.check.location': 'Purchase location',
  'gate.v1.gps.capture': 'Take location',
  'gate.v1.gps.recapture': 'Take again',
  'gate.v1.gps.locating': 'Getting a GPS lock…',
  'gate.v1.gps.holdStill': '±{m} m — hold the phone still…',
  'gate.v1.gps.notYet': 'Not taken yet. Needed to close the purchase.',
  'gate.v1.gps.fallback': 'GPS weak here: the last good location was used (±{m} m).',
  'gate.v1.gps.denied':
    'Location is blocked. Allow location for this app in the phone settings, then tap Take location.',
  'gate.v1.gps.noFix': 'No GPS signal. Step out under open sky and tap Take location.',
  'gate.v1.gps.skip': 'Close without location (supervisor)',
  'gate.v1.gps.skipped': 'Supervisor allowed closing without location',
  'gate.v1.gps.supReason': 'Close a purchase without its location',
  'gate.check.cards': 'A price for every lot',
} as const

export type GateMessageKey = keyof typeof gateEn
